// ---------------------------------------------------------------------------
// seo-comparison-locales.ts — Aggregates all non-English comparison locale files
// ---------------------------------------------------------------------------
import type { ComparisonConfig } from './ComparisonPage';

// P1 locales (Hotelogix, Cloudbeds, Mews)
import { COMPARISON_AR } from './seo-comparison.ar';
import { COMPARISON_TH } from './seo-comparison.th';
import { COMPARISON_ES } from './seo-comparison.es';
import { COMPARISON_FR } from './seo-comparison.fr';
import { COMPARISON_DE } from './seo-comparison.de';
import { COMPARISON_JA } from './seo-comparison.ja';
import { COMPARISON_ZH } from './seo-comparison.zh';
import { COMPARISON_HI } from './seo-comparison.hi';

// P2 locales (Opera, RMS Cloud)
import { COMPARISON_AR_P2 } from './seo-comparison-p2.ar';
import { COMPARISON_TH_P2 } from './seo-comparison-p2.th';
import { COMPARISON_ES_P2 } from './seo-comparison-p2.es';
import { COMPARISON_FR_P2 } from './seo-comparison-p2.fr';
import { COMPARISON_DE_P2 } from './seo-comparison-p2.de';
import { COMPARISON_JA_P2 } from './seo-comparison-p2.ja';
import { COMPARISON_ZH_P2 } from './seo-comparison-p2.zh';
import { COMPARISON_HI_P2 } from './seo-comparison-p2.hi';

// P3 locales (SiteMinder, RoomRaccoon, innRoad, WebRezPro)
import { COMPARISON_AR_P3 } from './seo-comparison-p3.ar';
import { COMPARISON_TH_P3 } from './seo-comparison-p3.th';
import { COMPARISON_ES_P3 } from './seo-comparison-p3.es';
import { COMPARISON_FR_P3 } from './seo-comparison-p3.fr';
import { COMPARISON_DE_P3 } from './seo-comparison-p3.de';
import { COMPARISON_JA_P3 } from './seo-comparison-p3.ja';
import { COMPARISON_ZH_P3 } from './seo-comparison-p3.zh';
import { COMPARISON_HI_P3 } from './seo-comparison-p3.hi';

// P4 locales (IDeaS, Duetto — AI-focused competitors)
import { P4_LOCALES } from './seo-comparison-p4.locales';

type LocaleMap = Record<string, Record<string, ComparisonConfig>>;

const P1: LocaleMap = {
  ar: COMPARISON_AR, th: COMPARISON_TH, es: COMPARISON_ES, fr: COMPARISON_FR,
  de: COMPARISON_DE, ja: COMPARISON_JA, zh: COMPARISON_ZH, hi: COMPARISON_HI,
};
const P2: LocaleMap = {
  ar: COMPARISON_AR_P2, th: COMPARISON_TH_P2, es: COMPARISON_ES_P2, fr: COMPARISON_FR_P2,
  de: COMPARISON_DE_P2, ja: COMPARISON_JA_P2, zh: COMPARISON_ZH_P2, hi: COMPARISON_HI_P2,
};
const P3: LocaleMap = {
  ar: COMPARISON_AR_P3, th: COMPARISON_TH_P3, es: COMPARISON_ES_P3, fr: COMPARISON_FR_P3,
  de: COMPARISON_DE_P3, ja: COMPARISON_JA_P3, zh: COMPARISON_ZH_P3, hi: COMPARISON_HI_P3,
};

/** Build locale entries for a slug from a locale map */
function localesFor(slug: string, map: LocaleMap): Record<string, ComparisonConfig> {
  const result: Record<string, ComparisonConfig> = {};
  for (const [locale, configs] of Object.entries(map)) {
    if (configs[slug]) result[locale] = configs[slug];
  }
  return result;
}

export function getP1Locales(slug: string) { return localesFor(slug, P1); }
export function getP2Locales(slug: string) { return localesFor(slug, P2); }
export function getP3Locales(slug: string) { return localesFor(slug, P3); }
export function getP4Locales(slug: string) { return localesFor(slug, P4_LOCALES); }

const AI_CATEGORY: Record<string, ComparisonConfig['featureTable'][number]> = {
  en: { category: 'AI & Intelligence', features: [
    { name: 'AI Dynamic Pricing Engine', hotelstack: true, competitor: false },
    { name: 'AI Chatbot & Voice Concierge', hotelstack: true, competitor: false },
    { name: 'Predictive Maintenance AI', hotelstack: true, competitor: false },
    { name: 'AI Guest Sentiment Analysis', hotelstack: true, competitor: false },
    { name: 'Digital Twin & AI Analytics', hotelstack: true, competitor: false },
  ]},
  ar: { category: 'الذكاء الاصطناعي', features: [
    { name: 'محرك التسعير الديناميكي AI', hotelstack: true, competitor: false },
    { name: 'روبوت دردشة ومساعد صوتي AI', hotelstack: true, competitor: false },
    { name: 'الصيانة التنبؤية AI', hotelstack: true, competitor: false },
    { name: 'تحليل مشاعر الضيوف AI', hotelstack: true, competitor: false },
    { name: 'التوأم الرقمي وتحليلات AI', hotelstack: true, competitor: false },
  ]},
  es: { category: 'IA e Inteligencia', features: [
    { name: 'Motor de Precios Dinámicos IA', hotelstack: true, competitor: false },
    { name: 'Chatbot y Concierge de Voz IA', hotelstack: true, competitor: false },
    { name: 'Mantenimiento Predictivo IA', hotelstack: true, competitor: false },
    { name: 'Análisis de Sentimiento IA', hotelstack: true, competitor: false },
    { name: 'Gemelo Digital y Analítica IA', hotelstack: true, competitor: false },
  ]},
  fr: { category: 'IA et Intelligence', features: [
    { name: 'Moteur de Tarification Dynamique IA', hotelstack: true, competitor: false },
    { name: 'Chatbot et Concierge Vocal IA', hotelstack: true, competitor: false },
    { name: 'Maintenance Prédictive IA', hotelstack: true, competitor: false },
    { name: 'Analyse de Sentiment IA', hotelstack: true, competitor: false },
    { name: 'Jumeau Numérique et Analytique IA', hotelstack: true, competitor: false },
  ]},
  de: { category: 'KI & Intelligenz', features: [
    { name: 'KI Dynamische Preisgestaltung', hotelstack: true, competitor: false },
    { name: 'KI Chatbot & Sprach-Concierge', hotelstack: true, competitor: false },
    { name: 'Prädiktive Wartung KI', hotelstack: true, competitor: false },
    { name: 'KI Gäste-Sentiment-Analyse', hotelstack: true, competitor: false },
    { name: 'Digital Twin & KI-Analytik', hotelstack: true, competitor: false },
  ]},
  ja: { category: 'AI＆インテリジェンス', features: [
    { name: 'AIダイナミックプライシング', hotelstack: true, competitor: false },
    { name: 'AIチャットボット＆音声コンシェルジュ', hotelstack: true, competitor: false },
    { name: '予測保全AI', hotelstack: true, competitor: false },
    { name: 'AIゲスト感情分析', hotelstack: true, competitor: false },
    { name: 'デジタルツイン＆AI分析', hotelstack: true, competitor: false },
  ]},
  zh: { category: 'AI与智能', features: [
    { name: 'AI动态定价引擎', hotelstack: true, competitor: false },
    { name: 'AI聊天机器人和语音礼宾', hotelstack: true, competitor: false },
    { name: '预测性维护AI', hotelstack: true, competitor: false },
    { name: 'AI客人情感分析', hotelstack: true, competitor: false },
    { name: '数字孪生与AI分析', hotelstack: true, competitor: false },
  ]},
  hi: { category: 'AI और बुद्धिमत्ता', features: [
    { name: 'AI डायनामिक प्राइसिंग इंजन', hotelstack: true, competitor: false },
    { name: 'AI चैटबॉट और वॉइस कंसीयज', hotelstack: true, competitor: false },
    { name: 'प्रेडिक्टिव मेंटेनेंस AI', hotelstack: true, competitor: false },
    { name: 'AI गेस्ट सेंटिमेंट एनालिसिस', hotelstack: true, competitor: false },
    { name: 'डिजिटल ट्विन और AI एनालिटिक्स', hotelstack: true, competitor: false },
  ]},
  th: { category: 'AI และปัญญาอัจฉริยะ', features: [
    { name: 'AI เครื่องมือกำหนดราคาแบบไดนามิก', hotelstack: true, competitor: false },
    { name: 'AI แชทบอทและ Voice Concierge', hotelstack: true, competitor: false },
    { name: 'การบำรุงรักษาเชิงคาดการณ์ AI', hotelstack: true, competitor: false },
    { name: 'AI การวิเคราะห์ความรู้สึกแขก', hotelstack: true, competitor: false },
    { name: 'Digital Twin และ AI Analytics', hotelstack: true, competitor: false },
  ]},
};

function r36(s: string): string { return s.replaceAll('36', '40+'); }

export function injectAI(config: ComparisonConfig, locale = 'en'): ComparisonConfig {
  const ai = AI_CATEGORY[locale] ?? AI_CATEGORY.en;
  return {
    ...config,
    heroSubtitle: r36(config.heroSubtitle),
    overviewComparison: config.overviewComparison.map(row =>
      row.hotelstack.includes('36')
        ? { ...row, hotelstack: r36(row.hotelstack) }
        : row,
    ),
    featureTable: [...config.featureTable, ai],
    pricingComparison: {
      ...config.pricingComparison,
      hotelstackNote: r36(config.pricingComparison.hotelstackNote),
    },
    advantages: config.advantages.map(r36),
    verdict: { ...config.verdict, summary: r36(config.verdict.summary) },
    faqItems: config.faqItems.map(f => ({
      ...f,
      answer: r36(f.answer),
    })),
  };
}

export function injectAILocaleMap(
  map: Record<string, ComparisonConfig>,
): Record<string, ComparisonConfig> {
  const result: Record<string, ComparisonConfig> = {};
  for (const [locale, config] of Object.entries(map)) {
    result[locale] = injectAI(config, locale);
  }
  return result;
}
